'use client'

import { useState } from 'react'
import { formatFontFamily } from '@/lib/themeHelpers'
import { CaretLeft, CaretRight } from 'phosphor-react'
import { useScrollAnimation, scrollAnimationClasses } from '@/lib/useScrollAnimation'

interface TestimonialsSectionProps {
  surface: "dark" | "light";
  theme: any;
  radius: number;
  fontFamily: string;
  gradStart: string;
  gradEnd: string;
  angle: number;
  content: any;
}

export default function TestimonialsSection({
  surface,
  theme,
  radius,
  fontFamily,
  gradStart,
  gradEnd,
  angle,
  content
}: TestimonialsSectionProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: cardRef, isVisible: cardVisible } = useScrollAnimation({ rootMargin: '0px 0px -50px 0px' });
  
  const backgroundStyle = {
    backgroundImage: surface === "dark" 
      ? `linear-gradient(${angle}deg, ${gradStart}, ${gradEnd})`
      : `linear-gradient(${angle}deg, #f8f9fa, #e9ecef)`,
    fontFamily: formatFontFamily(fontFamily)
  };

  const testimonials = content.testimonials || [];
  
  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };
  
  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  if (!testimonials || testimonials.length === 0) {
    return (
      <section className="relative w-full" style={backgroundStyle}>
        <div className="mx-auto max-w-6xl px-6 py-24 lg:px-8">
          <div className="text-center">
            <h2 className="text-4xl font-bold mb-4 font-primary" style={{ color: theme.text }}>
              {content.title}
            </h2>
            <p className="text-lg max-w-3xl mx-auto font-primary" style={{ color: theme.mutedText }}>
              No testimonials available.
            </p>
          </div>
        </div>
      </section>
    );
  }

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section className="relative w-full" style={backgroundStyle}>
      <div className="mx-auto max-w-6xl px-6 py-24 lg:px-8">
        {/* Header */}
        <div 
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-12 ${scrollAnimationClasses.transition} ${headerVisible ? scrollAnimationClasses.visible : scrollAnimationClasses.hidden}`}
        >
          <h2 className="text-4xl font-bold mb-4 font-primary" style={{ color: theme.text }}>
            {content.title}
          </h2>
          <p className="text-lg max-w-3xl mx-auto font-primary" style={{ color: theme.mutedText }}>
            {content.description}
          </p>
        </div>

        {/* Carousel Container */}
        <div 
          ref={cardRef as React.RefObject<HTMLDivElement>}
          className={`relative max-w-4xl mx-auto ${scrollAnimationClasses.transition} ${cardVisible ? scrollAnimationClasses.visible : scrollAnimationClasses.hidden}`}
          style={{ transitionDelay: '150ms' }}
        >
          {/* Testimonial Card */}
          <div 
            className="p-8 transition-all duration-300 ease-in-out"
            style={{
              borderRadius: radius,
              border: `1px solid ${theme.borderHover || theme.border}`,
              background: theme.card
            }}
          >
            {/* Quote */}
            <div className="mb-6">
              <div className="text-6xl mb-4 font-serif" style={{ color: '#F5CC5C' }}>"</div>
              <blockquote className="text-xl leading-relaxed font-primary" style={{ color: theme.text }}>
                {currentTestimonial.quote}
              </blockquote>
            </div>

            {/* Author Info */}
            <div className="flex items-center justify-between">
              <div>
                <div className="text-lg font-semibold mb-1 font-primary" style={{ color: theme.text }}>
                  {currentTestimonial.name}
                </div>
                <div className="text-base font-primary" style={{ color: theme.subtleText || theme.mutedText }}>
                  {currentTestimonial.title}
                </div>
                {currentTestimonial.company && (
                  <div className="text-sm font-primary" style={{ color: theme.subtleText || theme.mutedText }}>
                    {currentTestimonial.company}
                  </div>
                )}
              </div>
              
              {/* Results/Metrics */}
              {currentTestimonial.result && (
                <div className="text-right">
                  <div className="text-2xl font-bold font-primary" style={{ color: '#FFFFFF' }}>
                    {currentTestimonial.result}
                  </div>
                  {currentTestimonial.metric && (
                    <div className="text-sm font-primary" style={{ color: '#FFFFFF' }}>
                      {currentTestimonial.metric}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Bottom Navigation - Arrows and Dots */}
          {testimonials.length > 1 && (
            <div className="flex justify-center items-center mt-8 space-x-6">
              {/* Previous Button */}
              <button
                onClick={prevTestimonial}
                className="p-3 rounded-full transition-all duration-200 hover:scale-110"
                style={{
                  backgroundColor: '#000000',
                  border: 'none',
                  color: '#FFFFFF'
                }}
                aria-label="Previous testimonial"
              >
                <CaretLeft size={20} weight="bold" />
              </button>
              
              {/* Dot Indicators */}
              <div className="flex space-x-2">
                {testimonials.map((_: any, index: number) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className="w-3 h-3 rounded-full transition-all duration-200"
                    style={{
                      backgroundColor: index === currentIndex ? theme.primary : theme.border,
                      opacity: index === currentIndex ? 1 : 0.5
                    }}
                    aria-label={`Go to testimonial ${index + 1}`}
                  />
                ))}
              </div>
              
              {/* Next Button */}
              <button
                onClick={nextTestimonial}
                className="p-3 rounded-full transition-all duration-200 hover:scale-110"
                style={{
                  backgroundColor: '#000000',
                  border: 'none',
                  color: '#FFFFFF'
                }}
                aria-label="Next testimonial"
              >
                <CaretRight size={20} weight="bold" />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}